import React, { useState, useRef, useEffect, useCallback } from 'react';

function FlipBook({ images, imageUrls, highQualityImages, imageLoading, orientation = 'portrait', onImageClick, uploadedImageUrl }) {
  const [currentPage, setCurrentPage] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showThumbnails, setShowThumbnails] = useState(false);
  const [showTOC, setShowTOC] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const containerRef = useRef(null);
  const playIntervalRef = useRef(null);

  const isLandscape = orientation === 'landscape';

  const ITEMS_PER_PAGE = isLandscape ? 2 : 1;

  const productDetails = [
    { name: 'Premium Collection', sku: 'PRD-001', desc: 'High-quality branded merchandise for your team' },
    { name: 'Classic Edition', sku: 'PRD-002', desc: 'Timeless design with modern branding placement' },
    { name: 'Executive Series', sku: 'PRD-003', desc: 'Professional grade corporate gifting solution' },
    { name: 'Signature Line', sku: 'PRD-004', desc: 'Distinctive products that make an impression' },
    { name: 'Essential Pack', sku: 'PRD-005', desc: 'Everyday items with premium brand visibility' },
    { name: 'Limited Edition', sku: 'PRD-006', desc: 'Exclusive collection for special occasions' },
    { name: 'Sport Collection', sku: 'PRD-007', desc: 'Active lifestyle products with bold branding' },
    { name: 'Heritage Series', sku: 'PRD-008', desc: 'Classic craftsmanship meets modern design' },
  ];

  const pages = [{ type: 'cover' }];
  for (let i = 0; i < images.length; i += ITEMS_PER_PAGE) {
    pages.push({
      type: 'product',
      items: images.slice(i, i + ITEMS_PER_PAGE),
      startIndex: i,
    });
  }
  pages.push({ type: 'back' });

  const totalPages = pages.length;

  const goToPage = useCallback((pageIndex) => {
    if (isFlipping || pageIndex === currentPage) return;
    if (pageIndex < 0 || pageIndex >= totalPages) return;
    setIsFlipping(true);
    setCurrentPage(pageIndex);
    setTimeout(() => setIsFlipping(false), 700);
  }, [isFlipping, currentPage, totalPages]);

  const nextPage = useCallback(() => goToPage(currentPage + 1), [goToPage, currentPage]);
  const prevPage = useCallback(() => goToPage(currentPage - 1), [goToPage, currentPage]);

  useEffect(() => {
    if (isPlaying) {
      playIntervalRef.current = setInterval(() => {
        setCurrentPage(prev => {
          if (prev >= totalPages - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 3000);
    }
    return () => clearInterval(playIntervalRef.current);
  }, [isPlaying, totalPages]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const onFSChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFSChange);
    return () => document.removeEventListener('fullscreenchange', onFSChange);
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') nextPage();
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') prevPage();
      if (e.key === 'Escape') {
        setShowThumbnails(false);
        setShowTOC(false);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [nextPage, prevPage]);

  const getImageSrc = (image) => {
    if (!image) return null;
    return highQualityImages?.[image.id] || imageUrls?.[image.id] || image.url;
  };

  const renderPage = (page, pageIndex) => {
    if (page.type === 'cover') {
      return (
        <div className="ufb-page ufb-cover">
          <div className="ufb-cover-bg" />
          <div className="ufb-cover-content">
            <div className="ufb-cover-brand">
              {uploadedImageUrl ? (
                <img src={uploadedImageUrl} alt="Brand" className="ufb-cover-logo" />
              ) : (
                <div className="ufb-cover-logo-placeholder">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="m21 15-5-5L5 21" />
                  </svg>
                  <span>Your Logo</span>
                </div>
              )}
            </div>
            <div className="ufb-cover-divider" />
            <h1 className="ufb-cover-title">Product Lookbook</h1>
            <p className="ufb-cover-year">{new Date().getFullYear()} Collection</p>
          </div>
          <div className="ufb-page-edge" />
        </div>
      );
    }

    if (page.type === 'back') {
      return (
        <div className="ufb-page ufb-back-cover">
          <div className="ufb-cover-bg ufb-back-bg" />
          <div className="ufb-back-content">
            {uploadedImageUrl && (
              <img src={uploadedImageUrl} alt="Brand" className="ufb-back-logo" />
            )}
            <div className="ufb-back-divider" />
            <p className="ufb-back-tagline">Branded with precision.</p>
            <p className="ufb-back-cta">Contact us for custom orders</p>
          </div>
          <div className="ufb-page-edge" />
        </div>
      );
    }

    return (
      <div className={`ufb-page ufb-product-page ${isLandscape ? 'landscape' : 'portrait'}`}>
        <div className="ufb-page-inner">
          <div className="ufb-page-header">
            <div className="ufb-header-left">
              {uploadedImageUrl && <img src={uploadedImageUrl} alt="" className="ufb-header-logo" />}
            </div>
            <div className="ufb-header-right">
              <span className="ufb-page-num">{String(pageIndex).padStart(2, '0')}</span>
            </div>
          </div>

          <div className="ufb-products-area">
            {page.items.map((image, idx) => {
              const detail = productDetails[(page.startIndex + idx) % productDetails.length];
              const imgSrc = getImageSrc(image);
              const loading = imageLoading?.[image.id];

              return (
                <div key={image.id || idx} className="ufb-product-row">
                  <div
                    className="ufb-product-image-wrap"
                    onClick={() => onImageClick && onImageClick(imgSrc, image.id)}
                  >
                    {loading ? (
                      <div className="ufb-img-loading">
                        <div className="ufb-img-spinner" />
                      </div>
                    ) : (
                      <img src={imgSrc} alt={detail.name} className="ufb-product-image" />
                    )}
                  </div>
                  <div className="ufb-product-info">
                    <span className="ufb-product-sku">{detail.sku}</span>
                    <h3 className="ufb-product-name">{detail.name}</h3>
                    <p className="ufb-product-desc">{detail.desc}</p>
                    <div className="ufb-product-meta">
                      <span className="ufb-meta-dot" />
                      <span>Customizable</span>
                      <span className="ufb-meta-dot" />
                      <span>Branded</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="ufb-page-footer">
            <div className="ufb-footer-line" />
            <span className="ufb-footer-text">Product Lookbook &mdash; {new Date().getFullYear()}</span>
          </div>
        </div>
        <div className="ufb-page-edge" />
      </div>
    );
  };

  const thumbnailSrc = (page, idx) => {
    if (page.type === 'cover' || page.type === 'back') return uploadedImageUrl;
    if (page.items?.[0]) return getImageSrc(page.items[0]);
    return null;
  };

  return (
    <div className={`ufb-root ${isLandscape ? 'landscape' : 'portrait'}`} ref={containerRef}>
      {/* Dark background */}
      <div className="ufb-backdrop" />

      {/* Left/Right navigation arrows */}
      {currentPage > 0 && (
        <button className="ufb-arrow ufb-arrow-left" onClick={prevPage} disabled={isFlipping}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      )}
      {currentPage < totalPages - 1 && (
        <button className="ufb-arrow ufb-arrow-right" onClick={nextPage} disabled={isFlipping}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      )}

      {/* Book container */}
      <div className="ufb-book-stage">
        <div className={`ufb-book ${isFlipping ? 'ufb-flipping' : ''}`}>
          {renderPage(pages[currentPage], currentPage)}
        </div>
        {/* Book shadow on surface */}
        <div className="ufb-book-shadow" />
      </div>

      {/* Page selector (bottom bar shows current / total) */}
      <div className="ufb-page-selector">
        <span>{currentPage + 1}</span>
        <span className="ufb-page-sep">/</span>
        <span>{totalPages}</span>
      </div>

      {/* Toolbar */}
      <div className="ufb-toolbar">
        <div className="ufb-toolbar-inner">
          {/* Pages Grid */}
          <button
            className={`ufb-tb-btn ${showThumbnails ? 'active' : ''}`}
            onClick={() => { setShowThumbnails(!showThumbnails); setShowTOC(false); }}
            title="Pages"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <rect x="3" y="3" width="7" height="7" rx="1.5" />
              <rect x="14" y="3" width="7" height="7" rx="1.5" />
              <rect x="3" y="14" width="7" height="7" rx="1.5" />
              <rect x="14" y="14" width="7" height="7" rx="1.5" />
            </svg>
          </button>

          {/* Table of Contents */}
          <button
            className={`ufb-tb-btn ${showTOC ? 'active' : ''}`}
            onClick={() => { setShowTOC(!showTOC); setShowThumbnails(false); }}
            title="Table of Contents"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="15" y2="12" />
              <line x1="3" y1="18" x2="18" y2="18" />
            </svg>
          </button>

          <div className="ufb-tb-sep" />

          {/* Zoom Out */}
          <button className="ufb-tb-btn" onClick={prevPage} title="Previous Page" disabled={currentPage === 0}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="8" y1="11" x2="14" y2="11" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>

          {/* Zoom In */}
          <button className="ufb-tb-btn" onClick={nextPage} title="Next Page" disabled={currentPage >= totalPages - 1}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="11" y1="8" x2="11" y2="14" />
              <line x1="8" y1="11" x2="14" y2="11" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>

          <div className="ufb-tb-sep" />

          {/* Play / Pause */}
          <button
            className={`ufb-tb-btn ${isPlaying ? 'active' : ''}`}
            onClick={() => setIsPlaying(!isPlaying)}
            title={isPlaying ? 'Pause' : 'Play Slideshow'}
          >
            {isPlaying ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
            )}
          </button>

          <div className="ufb-tb-sep" />

          {/* Share */}
          <button className="ufb-tb-btn" title="Share" onClick={() => {
            if (navigator.share) navigator.share({ title: 'Product Lookbook', url: window.location.href });
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
          </button>

          {/* Print */}
          <button className="ufb-tb-btn" title="Print" onClick={() => window.print()}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
          </button>

          <div className="ufb-tb-sep" />

          {/* Fullscreen */}
          <button className="ufb-tb-btn" onClick={toggleFullscreen} title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
            {isFullscreen ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="4 14 10 14 10 20" />
                <polyline points="20 10 14 10 14 4" />
                <line x1="14" y1="10" x2="21" y2="3" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Thumbnails Panel */}
      {showThumbnails && (
        <div className="ufb-panel ufb-thumbnails-panel">
          <div className="ufb-panel-header">
            <span>Pages</span>
            <button className="ufb-panel-close" onClick={() => setShowThumbnails(false)}>&times;</button>
          </div>
          <div className="ufb-thumbnails-grid">
            {pages.map((page, i) => (
              <div
                key={i}
                className={`ufb-thumb ${i === currentPage ? 'active' : ''}`}
                onClick={() => { goToPage(i); setShowThumbnails(false); }}
              >
                <div className="ufb-thumb-img">
                  {page.type === 'cover' ? (
                    <div className="ufb-thumb-label">Cover</div>
                  ) : page.type === 'back' ? (
                    <div className="ufb-thumb-label">Back</div>
                  ) : thumbnailSrc(page, i) ? (
                    <img src={thumbnailSrc(page, i)} alt="" />
                  ) : (
                    <div className="ufb-thumb-label">Page {i}</div>
                  )}
                </div>
                <span className="ufb-thumb-num">{i + 1}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table of Contents Panel */}
      {showTOC && (
        <div className="ufb-panel ufb-toc-panel">
          <div className="ufb-panel-header">
            <span>Table of Contents</span>
            <button className="ufb-panel-close" onClick={() => setShowTOC(false)}>&times;</button>
          </div>
          <div className="ufb-toc-list">
            <div className={`ufb-toc-item ${currentPage === 0 ? 'active' : ''}`} onClick={() => { goToPage(0); setShowTOC(false); }}>
              <span className="ufb-toc-num">01</span>
              <span>Cover</span>
            </div>
            {pages.filter(p => p.type === 'product').map((page, idx) => (
              <div
                key={idx}
                className={`ufb-toc-item ${currentPage === idx + 1 ? 'active' : ''}`}
                onClick={() => { goToPage(idx + 1); setShowTOC(false); }}
              >
                <span className="ufb-toc-num">{String(idx + 2).padStart(2, '0')}</span>
                <span>{productDetails[(page.startIndex) % productDetails.length]?.name || `Page ${idx + 2}`}</span>
              </div>
            ))}
            <div className={`ufb-toc-item ${currentPage === totalPages - 1 ? 'active' : ''}`} onClick={() => { goToPage(totalPages - 1); setShowTOC(false); }}>
              <span className="ufb-toc-num">{String(totalPages).padStart(2, '0')}</span>
              <span>Back Cover</span>
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* ============ ROOT ============ */
        .ufb-root {
          position: relative;
          width: 100%;
          height: 85vh;
          min-height: 500px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          user-select: none;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        .ufb-root:-webkit-full-screen,
        .ufb-root:fullscreen {
          height: 100vh;
          background: #0a0a0a;
        }

        .ufb-backdrop {
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at center, #1a1a2e 0%, #0d0d15 70%, #050508 100%);
          z-index: 0;
        }

        /* ============ ARROWS ============ */
        .ufb-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 20;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 50%;
          width: 52px;
          height: 52px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255,255,255,0.5);
          cursor: pointer;
          transition: all 0.3s ease;
          backdrop-filter: blur(8px);
        }
        .ufb-arrow:hover { background: rgba(255,255,255,0.12); color: #fff; }
        .ufb-arrow:disabled { opacity: 0; pointer-events: none; }
        .ufb-arrow-left { left: 20px; }
        .ufb-arrow-right { right: 20px; }
        @media (max-width: 700px) {
          .ufb-arrow { width: 40px; height: 40px; }
          .ufb-arrow-left { left: 8px; }
          .ufb-arrow-right { right: 8px; }
        }

        /* ============ BOOK STAGE ============ */
        .ufb-book-stage {
          position: relative;
          z-index: 10;
          perspective: 2000px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .ufb-book {
          transform: rotateY(-2deg) rotateX(2deg);
          transform-style: preserve-3d;
          transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);
          will-change: transform;
        }
        .ufb-book.ufb-flipping {
          animation: ufbFlip 0.7s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .ufb-root.portrait .ufb-book {
          width: min(420px, 80vw);
          height: min(580px, 72vh);
        }
        .ufb-root.landscape .ufb-book {
          width: min(620px, 85vw);
          height: min(440px, 62vh);
        }

        @keyframes ufbFlip {
          0% { transform: rotateY(-2deg) rotateX(2deg) scale(1); }
          30% { transform: rotateY(-8deg) rotateX(3deg) scale(0.97); }
          60% { transform: rotateY(4deg) rotateX(1deg) scale(0.97); }
          100% { transform: rotateY(-2deg) rotateX(2deg) scale(1); }
        }

        .ufb-book-shadow {
          width: 90%;
          height: 30px;
          margin-top: -4px;
          background: radial-gradient(ellipse at center, rgba(0,0,0,0.5) 0%, transparent 70%);
          filter: blur(10px);
        }

        /* ============ PAGE ============ */
        .ufb-page {
          width: 100%;
          height: 100%;
          border-radius: 4px 12px 12px 4px;
          overflow: hidden;
          position: relative;
          box-shadow:
            8px 4px 20px rgba(0,0,0,0.35),
            2px 1px 6px rgba(0,0,0,0.2),
            inset -3px 0 12px rgba(0,0,0,0.06);
        }

        .ufb-page-edge {
          position: absolute;
          left: 0;
          top: 0;
          width: 6px;
          height: 100%;
          background: linear-gradient(90deg, #d4d0c8 0%, #e8e4dc 40%, #f0ece4 100%);
          border-radius: 4px 0 0 4px;
          box-shadow: inset -1px 0 3px rgba(0,0,0,0.08);
        }

        /* ============ COVER ============ */
        .ufb-cover-bg {
          position: absolute;
          inset: 0;
          background: linear-gradient(155deg, #0f172a 0%, #1e293b 35%, #0f172a 100%);
        }
        .ufb-back-bg {
          background: linear-gradient(155deg, #1e293b 0%, #0f172a 50%, #1e293b 100%);
        }
        .ufb-cover-content, .ufb-back-content {
          position: relative;
          z-index: 2;
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 16px;
          padding: 40px 32px;
        }
        .ufb-cover-brand { display: flex; align-items: center; justify-content: center; }
        .ufb-cover-logo {
          max-width: 180px;
          max-height: 130px;
          object-fit: contain;
          filter: drop-shadow(0 4px 16px rgba(0,0,0,0.4));
        }
        .ufb-cover-logo-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          color: rgba(255,255,255,0.3);
          border: 2px dashed rgba(255,255,255,0.15);
          border-radius: 12px;
          padding: 24px 40px;
          font-size: 13px;
          letter-spacing: 2px;
          text-transform: uppercase;
        }
        .ufb-cover-divider {
          width: 50px;
          height: 2px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          margin: 4px 0;
        }
        .ufb-cover-title {
          font-size: clamp(22px, 4vw, 32px);
          font-weight: 300;
          color: #fff;
          letter-spacing: 6px;
          text-transform: uppercase;
          margin: 0;
          text-align: center;
        }
        .ufb-cover-year {
          font-size: 12px;
          color: rgba(255,255,255,0.4);
          letter-spacing: 4px;
          text-transform: uppercase;
          margin: 0;
        }

        .ufb-back-logo {
          max-width: 80px;
          max-height: 50px;
          object-fit: contain;
          opacity: 0.5;
        }
        .ufb-back-divider {
          width: 30px;
          height: 1px;
          background: rgba(255,255,255,0.15);
        }
        .ufb-back-tagline {
          font-size: 15px;
          color: rgba(255,255,255,0.4);
          letter-spacing: 3px;
          text-transform: uppercase;
          margin: 0;
          font-weight: 300;
        }
        .ufb-back-cta {
          font-size: 11px;
          color: rgba(255,255,255,0.25);
          letter-spacing: 1px;
          margin: 0;
        }

        /* ============ PRODUCT PAGE ============ */
        .ufb-product-page {
          background: #fdfdfc;
          border-radius: 4px 12px 12px 4px;
        }
        .ufb-page-inner {
          padding: 20px 24px 16px 28px;
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .ufb-page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 12px;
          border-bottom: 1px solid #eee;
          flex-shrink: 0;
        }
        .ufb-header-logo {
          max-height: 22px;
          max-width: 60px;
          object-fit: contain;
          opacity: 0.7;
        }
        .ufb-page-num {
          font-size: 11px;
          color: #bbb;
          letter-spacing: 2px;
          font-weight: 500;
        }

        .ufb-products-area {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 16px;
          padding: 12px 0;
          overflow: hidden;
        }

        .ufb-product-row {
          display: flex;
          gap: 20px;
          align-items: center;
        }
        .ufb-root.portrait .ufb-product-row {
          flex-direction: column;
          text-align: center;
        }
        .ufb-root.landscape .ufb-product-row {
          flex-direction: row;
        }

        .ufb-product-image-wrap {
          flex-shrink: 0;
          border-radius: 8px;
          overflow: hidden;
          background: #f5f5f3;
          cursor: pointer;
          transition: transform 0.25s ease, box-shadow 0.25s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .ufb-product-image-wrap:hover {
          transform: scale(1.03);
          box-shadow: 0 6px 24px rgba(0,0,0,0.1);
        }
        .ufb-root.portrait .ufb-product-image-wrap {
          width: 100%;
          max-height: 55%;
          aspect-ratio: 4/3;
        }
        .ufb-root.landscape .ufb-product-image-wrap {
          width: 50%;
          aspect-ratio: 1;
        }

        .ufb-product-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 12px;
        }

        .ufb-img-loading {
          width: 100%;
          height: 100%;
          min-height: 120px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .ufb-img-spinner {
          width: 28px;
          height: 28px;
          border: 3px solid #e8e8e8;
          border-top-color: #1e293b;
          border-radius: 50%;
          animation: ufbSpin 0.7s linear infinite;
        }
        @keyframes ufbSpin { to { transform: rotate(360deg); } }

        .ufb-product-info {
          display: flex;
          flex-direction: column;
          gap: 6px;
          min-width: 0;
        }
        .ufb-product-sku {
          font-size: 10px;
          color: #aaa;
          letter-spacing: 2px;
          text-transform: uppercase;
          font-weight: 600;
        }
        .ufb-product-name {
          font-size: clamp(16px, 2.5vw, 20px);
          font-weight: 600;
          color: #1a1a1a;
          margin: 0;
          line-height: 1.2;
        }
        .ufb-product-desc {
          font-size: clamp(11px, 1.8vw, 13px);
          color: #777;
          line-height: 1.5;
          margin: 0;
        }
        .ufb-product-meta {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 4px;
          font-size: 10px;
          color: #999;
          letter-spacing: 1px;
          text-transform: uppercase;
        }
        .ufb-meta-dot {
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: #ccc;
        }

        .ufb-page-footer {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          gap: 12px;
          padding-top: 10px;
        }
        .ufb-footer-line {
          flex: 1;
          height: 1px;
          background: #eee;
        }
        .ufb-footer-text {
          font-size: 9px;
          color: #ccc;
          letter-spacing: 2px;
          text-transform: uppercase;
          white-space: nowrap;
        }

        /* ============ PAGE SELECTOR ============ */
        .ufb-page-selector {
          position: absolute;
          bottom: 72px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 15;
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 13px;
          color: rgba(255,255,255,0.4);
          letter-spacing: 1px;
          font-weight: 500;
        }
        .ufb-page-sep { opacity: 0.4; }

        /* ============ TOOLBAR ============ */
        .ufb-toolbar {
          position: absolute;
          bottom: 16px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 30;
        }
        .ufb-toolbar-inner {
          display: flex;
          align-items: center;
          gap: 4px;
          background: rgba(25, 25, 25, 0.7);
          backdrop-filter: blur(16px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 12px;
          padding: 6px 10px;
        }
        .ufb-tb-btn {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          border: none;
          background: transparent;
          color: rgba(255,255,255,0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .ufb-tb-btn:hover { color: #fff; background: rgba(255,255,255,0.08); }
        .ufb-tb-btn.active { color: #7FDC23; }
        .ufb-tb-btn:disabled { opacity: 0.25; cursor: default; }
        .ufb-tb-sep {
          width: 1px;
          height: 20px;
          background: rgba(255,255,255,0.1);
          margin: 0 4px;
        }

        @media (max-width: 550px) {
          .ufb-toolbar-inner { gap: 2px; padding: 4px 6px; }
          .ufb-tb-btn { width: 30px; height: 30px; }
          .ufb-tb-btn svg { width: 15px; height: 15px; }
        }

        /* ============ PANELS ============ */
        .ufb-panel {
          position: absolute;
          left: 0;
          top: 0;
          bottom: 60px;
          width: 260px;
          background: rgba(20, 20, 20, 0.95);
          backdrop-filter: blur(20px);
          border-right: 1px solid rgba(255,255,255,0.06);
          z-index: 40;
          display: flex;
          flex-direction: column;
          animation: ufbSlideIn 0.25s ease;
        }
        @keyframes ufbSlideIn {
          from { transform: translateX(-100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .ufb-panel-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 16px 12px;
          border-bottom: 1px solid rgba(255,255,255,0.06);
          font-size: 13px;
          font-weight: 600;
          color: #fff;
          letter-spacing: 1px;
          text-transform: uppercase;
        }
        .ufb-panel-close {
          background: none;
          border: none;
          color: rgba(255,255,255,0.4);
          font-size: 20px;
          cursor: pointer;
          padding: 0 4px;
          line-height: 1;
        }
        .ufb-panel-close:hover { color: #fff; }

        /* Thumbnails */
        .ufb-thumbnails-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
          padding: 12px;
          overflow-y: auto;
        }
        .ufb-thumb {
          cursor: pointer;
          border-radius: 6px;
          overflow: hidden;
          border: 2px solid transparent;
          transition: border-color 0.2s;
          text-align: center;
        }
        .ufb-thumb.active { border-color: #7FDC23; }
        .ufb-thumb:hover { border-color: rgba(255,255,255,0.2); }
        .ufb-thumb-img {
          aspect-ratio: 3/4;
          background: rgba(255,255,255,0.04);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        .ufb-thumb-img img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .ufb-thumb-label {
          font-size: 10px;
          color: rgba(255,255,255,0.3);
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .ufb-thumb-num {
          display: block;
          font-size: 10px;
          color: rgba(255,255,255,0.35);
          padding: 4px 0;
        }

        /* TOC */
        .ufb-toc-list {
          overflow-y: auto;
          padding: 8px 0;
        }
        .ufb-toc-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 16px;
          cursor: pointer;
          color: rgba(255,255,255,0.45);
          font-size: 13px;
          transition: all 0.2s;
          border-left: 2px solid transparent;
        }
        .ufb-toc-item:hover { color: #fff; background: rgba(255,255,255,0.04); }
        .ufb-toc-item.active { color: #7FDC23; border-left-color: #7FDC23; }
        .ufb-toc-num {
          font-size: 11px;
          font-weight: 600;
          opacity: 0.5;
          min-width: 20px;
        }
      `}</style>
    </div>
  );
}

export default FlipBook;
